// Content preview model: one normalized shape for previewing any brief (blog post, social post,
// video) the way its audience will see it. Built from the brief, its latest draft, the selected
// visual slots (PRD-0191), the brand and the brand visual kit. Renderers (frontend
// components/preview) take only this model, so a new surface needs a renderer, not new plumbing.
// Pure functions only (shared by frontend and backend).
import { canonicalSuggestion, plainText, readerBody, seoDescription, siteOrigin, slugify } from "./blog-render";
import type { BrandVisualKit, BriefVisuals, FontRole, PaletteRole, VisualKitFontFile } from "./visuals";

export type PreviewDevice = "desktop" | "mobile";
export const PREVIEW_DEVICES: readonly PreviewDevice[] = ["desktop", "mobile"];

/** Broad family a surface belongs to; picks the default renderer family. */
export type PreviewContentType = "blog" | "social" | "video";

export type PreviewMediaRole = "hero" | "og" | "image" | "video" | "thumbnail";

export interface PreviewMedia {
  role: PreviewMediaRole;
  /** Browser-loadable URL (signed Mech Storage URL or a public URL). */
  url: string;
  alt: string;
  width: number | null;
  height: number | null;
}

/** Who the content is from, and how the brand's own site looks (kit). */
export interface PreviewIdentity {
  name: string;
  website_url: string | null;
  /** `brand.example` (no scheme, no www). */
  domain: string | null;
  logo_url: string | null;
  has_kit: boolean;
  /** Palette roles from the kit; missing roles fall back to neutral defaults in renderers. */
  palette: Partial<Record<PaletteRole, string>>;
  /** Declared font families by role (kit typography). */
  fonts: Partial<Record<FontRole, string>>;
  /** Loadable kit font files (signed, short-lived). */
  font_files: VisualKitFontFile[];
  /** Wordmark text for the site header (kit), else the brand name. */
  wordmark: string;
  eyebrow: string | null;
}

/** The card a shared link unfurls to (og:title / og:description / og:image). */
export interface PreviewLink {
  url: string | null;
  title: string;
  description: string;
  domain: string | null;
  image: PreviewMedia | null;
}

export type PreviewWarningCode = "not_approved" | "missing_media" | "stale_card" | "no_content";
export interface PreviewWarning {
  code: PreviewWarningCode;
  message: string;
}

export interface PreviewModel {
  brief_id: string;
  brand_id: string;
  campaign_id: string;
  /** Lower-case surface (`blog`, `linkedin`, `youtube`, ...); `unknown` when the brief has none. */
  surface: string;
  content_type: PreviewContentType;
  state: string;
  title: string;
  /** Reader-facing markdown: chips, internal notes and a title-repeating H1 removed (same as export). */
  body: string;
  /** Plain text of the body with line breaks kept, for captions and post text. */
  text: string;
  /** First prose paragraph, at most 160 characters (same as the export's SEO description). */
  description: string;
  media: PreviewMedia[];
  identity: PreviewIdentity;
  link: PreviewLink;
  /** ISO timestamp shown as the post date (published, else scheduled, else last updated). */
  date: string | null;
  published_url: string | null;
  warnings: PreviewWarning[];
}

/** The brief fields a preview needs (Brief and ReviewApprovalItem both satisfy this). */
export interface PreviewBriefInput {
  id: string;
  campaign_id: string;
  brand_id: string;
  title: string;
  state: string;
  surface?: string | null;
  platform?: string | null;
  published_at?: string | null;
  published_url?: string | null;
  scheduled_at?: string | null;
  updated_at?: string | null;
  thumbnail_url?: string | null;
  rendered_asset_url?: string | null;
}

export interface PreviewBuildInput {
  brief: PreviewBriefInput;
  /** Latest draft content (markdown or plain text); null when there is none yet. */
  content: string | null;
  visuals?: Pick<BriefVisuals, "slots"> | null;
  brand?: { name?: string | null; website_url?: string | null; logo_url?: string | null } | null;
  kit?: BrandVisualKit | null;
}

const BLOG_SURFACES = new Set(["blog", "website"]);
const VIDEO_SURFACES = new Set(["youtube", "youtube_shorts", "video"]);
/** States in which content counts as approved for publishing. */
export const APPROVED_PREVIEW_STATES = new Set(["approved", "scheduled", "published"]);
/** Surfaces whose posts are image-first: a post without an image is not publishable as-is. */
const IMAGE_FIRST_SURFACES = new Set(["instagram", "pinterest"]);

export function previewSurface(brief: Pick<PreviewBriefInput, "surface" | "platform">): string {
  return (brief.surface ?? brief.platform ?? "").trim().toLowerCase() || "unknown";
}

export function contentTypeFor(surface: string): PreviewContentType {
  if (BLOG_SURFACES.has(surface)) return "blog";
  if (VIDEO_SURFACES.has(surface)) return "video";
  return "social";
}

/** `brand.example` from a website setting; null when unusable. */
export function displayDomain(siteUrl: string | null | undefined): string | null {
  const origin = siteOrigin(siteUrl);
  return origin ? new URL(origin).hostname.replace(/^www\./, "") : null;
}

/** Markdown → readable plain text that keeps paragraphs and line breaks (post text, captions). */
export function previewText(markdown: string): string {
  return markdown
    .split(/\n{2,}/)
    .map((block) => block.split("\n").map((line) => plainText(line)).filter(Boolean).join("\n"))
    .filter(Boolean)
    .join("\n\n");
}

function slotMedia(entry: BriefVisuals["slots"]["hero"]["selected"] | undefined, role: PreviewMediaRole, fallbackAlt: string): PreviewMedia | null {
  const asset = entry?.asset;
  if (!asset?.url) return null;
  return { role, url: asset.url, alt: asset.alt_text ?? fallbackAlt, width: asset.width, height: asset.height };
}

function isHttpUrl(value: string | null | undefined): value is string {
  return !!value && /^https?:\/\//i.test(value.trim());
}

export function buildPreviewModel(input: PreviewBuildInput): PreviewModel {
  const { brief, kit } = input;
  const surface = previewSurface(brief);
  const contentType = contentTypeFor(surface);
  const title = brief.title?.trim() || "Untitled";
  const body = input.content ? readerBody(input.content, title) : "";
  const text = previewText(body);
  const description = body ? seoDescription(body) : "";
  const hero = slotMedia(input.visuals?.slots.hero.selected, "hero", `Illustration for “${title}”`);
  const og = slotMedia(input.visuals?.slots.og.selected, "og", title);
  const media: PreviewMedia[] = [];
  if (hero) media.push(hero);
  if (og) media.push(og);
  if (isHttpUrl(brief.thumbnail_url)) media.push({ role: "thumbnail", url: brief.thumbnail_url, alt: title, width: null, height: null });
  if (isHttpUrl(brief.rendered_asset_url) && contentType === "video") media.push({ role: "video", url: brief.rendered_asset_url, alt: title, width: null, height: null });

  const name = input.brand?.name?.trim() || "Your brand";
  const websiteUrl = input.brand?.website_url?.trim() || null;
  const domain = displayDomain(websiteUrl);
  const identity: PreviewIdentity = {
    name,
    website_url: websiteUrl,
    domain,
    logo_url: isHttpUrl(input.brand?.logo_url) ? input.brand!.logo_url! : null,
    has_kit: !!kit,
    palette: kit?.palette ?? {},
    fonts: kit?.typography ?? {},
    font_files: kit?.font_files ?? [],
    wordmark: kit?.templates.wordmark?.trim() || name,
    eyebrow: kit?.templates.eyebrow?.trim() || null,
  };

  const linkUrl = brief.published_url?.trim() || (contentType === "blog" ? canonicalSuggestion(websiteUrl, slugify(title)) : websiteUrl);
  const link: PreviewLink = {
    url: linkUrl || null,
    title,
    description,
    domain: displayDomain(linkUrl) ?? domain,
    image: og ?? hero,
  };

  const warnings: PreviewWarning[] = [];
  if (!APPROVED_PREVIEW_STATES.has(brief.state)) {
    warnings.push({ code: "not_approved", message: "This is a draft preview — the content isn't approved yet." });
  }
  if (!body.trim()) warnings.push({ code: "no_content", message: "There is no draft yet, so the preview shows the title only." });
  if (contentType === "blog" && !hero) {
    warnings.push({ code: "missing_media", message: "No hero image selected — choose one under Images on the post." });
  } else if (contentType === "video" && !media.some((item) => item.role === "thumbnail" || item.role === "hero" || item.role === "og")) {
    warnings.push({ code: "missing_media", message: "No thumbnail yet — the video preview shows a placeholder." });
  } else if (IMAGE_FIRST_SURFACES.has(surface) && media.length === 0) {
    warnings.push({ code: "missing_media", message: "This surface needs an image, and none is attached yet." });
  }
  if (input.visuals?.slots.og.selected?.stale) {
    warnings.push({ code: "stale_card", message: "The social card was made from a different hero image — re-render it before publishing." });
  }

  return {
    brief_id: brief.id,
    brand_id: brief.brand_id,
    campaign_id: brief.campaign_id,
    surface,
    content_type: contentType,
    state: brief.state,
    title,
    body,
    text,
    description,
    media,
    identity,
    link,
    date: brief.published_at ?? brief.scheduled_at ?? brief.updated_at ?? null,
    published_url: brief.published_url ?? null,
    warnings,
  };
}

/** First media of the given roles, in priority order. */
export function pickMedia(model: Pick<PreviewModel, "media">, roles: readonly PreviewMediaRole[]): PreviewMedia | null {
  for (const role of roles) {
    const found = model.media.find((item) => item.role === role);
    if (found) return found;
  }
  return null;
}
